import type {
  AddPMCommentConfirmation,
  CreatePMIssueConfirmation,
  PMIssueDiff,
  PMScheduleBatch,
  PMScheduleEffect,
  PMScheduleMove,
  PMScheduleWarning,
  UpdatePMIssueConfirmation,
} from "@/stores/chat/repository";

interface PMUser {
  first_name: string | null;
  last_name: string | null;
  email: string;
}

interface PMDiscipline {
  name: string;
  abbreviation: string | null;
}

interface PMParent {
  issue_number: number;
  title: string;
}

function userLabel(u: PMUser): string {
  const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return name ? `${name} (${u.email})` : u.email;
}

function disciplineLabel(d: PMDiscipline): string {
  return d.abbreviation ? `${d.name} (${d.abbreviation})` : d.name;
}

function parentLabel(p: PMParent | null): string {
  return p ? `#${p.issue_number} — ${p.title}` : "—";
}

type PMConfirmationViewProps =
  | { variant: "create"; payload: CreatePMIssueConfirmation }
  | { variant: "update"; payload: UpdatePMIssueConfirmation }
  | { variant: "comment"; payload: AddPMCommentConfirmation };

export function PMIssueConfirmationView(props: PMConfirmationViewProps) {
  if (props.variant === "create") {
    return <CreateIssueView payload={props.payload} />;
  }
  if (props.variant === "update") {
    return <UpdateIssueView payload={props.payload} />;
  }
  return <AddCommentView payload={props.payload} />;
}

function CreateIssueView({ payload }: { payload: CreatePMIssueConfirmation }) {
  const { issue, summary } = payload;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="font-semibold text-neutral-800">{summary}</div>
      <Row label="Title">{issue.title}</Row>
      <Row label="Status">{labelForStatus(issue.status)}</Row>
      {issue.start_date ? <Row label="Start">{issue.start_date}</Row> : null}
      {issue.due_date ? <Row label="Due">{issue.due_date}</Row> : null}
      {issue.project ? <Row label="Project">{issue.project}</Row> : null}
      {issue.disciplines.length > 0 ? (
        <Row label="Disciplines">{issue.disciplines.map(disciplineLabel).join(", ")}</Row>
      ) : null}
      {issue.assignees.length > 0 ? (
        <Row label="Assignees">{issue.assignees.map(userLabel).join(", ")}</Row>
      ) : null}
      {issue.linked_documents.length > 0 ? (
        <Row label="Linked documents">{issue.linked_documents.join(", ")}</Row>
      ) : null}
      {issue.parent_issue ? (
        <Row label="Parent issue">{parentLabel(issue.parent_issue)}</Row>
      ) : null}
    </div>
  );
}

function UpdateIssueView({ payload }: { payload: UpdatePMIssueConfirmation }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="font-semibold text-neutral-800">
        {payload.summary}
        {payload.issue_number ? (
          <span className="ml-1.5 rounded-md border border-neutral-300 bg-white px-1.5 py-0.5 text-[11px] text-neutral-600">
            #{payload.issue_number}
          </span>
        ) : null}
      </div>
      {payload.diff.length > 0 ? (
        <ul className="ml-2 flex list-disc flex-col gap-1 pl-3 text-neutral-700">
          {payload.diff.map((row, idx) => (
            <li key={`${row.field}-${idx}`}>
              <DiffRow row={row} />
            </li>
          ))}
        </ul>
      ) : null}
      {payload.batch ? <BatchView batch={payload.batch} /> : null}
      {!payload.batch && payload.schedule ? <ScheduleEffect effect={payload.schedule} /> : null}
    </div>
  );
}

function dates(start: string | null, due: string | null): string {
  return `${start ?? "no start"} – ${due ?? "no due date"}`;
}

function MoveRow({ move }: { move: PMScheduleMove }) {
  return (
    <>
      <span className="font-medium">
        #{move.issue_number} {move.title}:
      </span>{" "}
      {/* Each range stays whole; the line breaks between them, not inside a date. */}
      <span className="whitespace-nowrap text-neutral-500">
        {dates(move.old_start, move.old_due)}
      </span>{" "}
      <span className="text-neutral-400">→</span>{" "}
      <span className="whitespace-nowrap text-primary">{dates(move.new_start, move.new_due)}</span>
    </>
  );
}

function MoveList({ moves }: { moves: PMScheduleMove[] }) {
  return (
    <ul className="ml-2 flex list-disc flex-col gap-1 pl-3 text-neutral-700">
      {moves.map((move) => (
        <li key={move.issue_number}>
          <MoveRow move={move} />
        </li>
      ))}
    </ul>
  );
}

function warningText(warning: PMScheduleWarning): string {
  const issue = warning.issue_number != null ? `#${warning.issue_number}` : "An issue";
  if (warning.kind === "soft_violation") {
    const after =
      warning.predecessor_issue_number != null ? ` after #${warning.predecessor_issue_number}` : "";
    const from = warning.required_start
      ? `, which needs it to start on ${warning.required_start} or later`
      : "";
    return `${issue} breaks its Flexible link${after}${from}. It will not move on its own.`;
  }
  return `Scheduling warning (${warning.kind}) for ${issue}.`;
}

/** The knock-on effect of a date change: what else moves, and the end date. */
function ScheduleEffect({ effect }: { effect: PMScheduleEffect }) {
  const hidden = effect.moved_total - effect.moved.length;
  const { completion } = effect;
  const pinned = effect.warnings.filter((w) => w.kind === "pinned").length;
  // Everything but `pinned` is listed on its own line. This card is where the
  // user approves the write, so a warning the card cannot word still shows.
  const others = effect.warnings.filter((w) => w.kind !== "pinned");
  // The finish row follows the dates themselves; `delta_days` is optional.
  const finishMoves = (completion.old ?? null) !== (completion.new ?? null);
  return (
    <>
      {effect.moved_total > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="font-medium text-neutral-600">Also moves ({effect.moved_total}):</span>
          <MoveList moves={effect.moved} />
          {hidden > 0 ? <span className="ml-5 text-neutral-500">and {hidden} more</span> : null}
        </div>
      ) : null}
      {finishMoves ? (
        <Row label="Project finish">
          {completion.old ?? "—"} → {completion.new ?? "—"}
        </Row>
      ) : null}
      {pinned > 0 ? (
        <div className="text-[12px] text-amber-700">
          {pinned === 1
            ? "1 hand-scheduled issue will not move with it."
            : `${pinned} hand-scheduled issues will not move with it.`}
        </div>
      ) : null}
      {others.map((warning, index) => (
        <div
          key={`${warning.kind}-${warning.issue_number ?? index}`}
          className="text-[12px] text-amber-700"
        >
          {warningText(warning)}
        </div>
      ))}
    </>
  );
}

function ChangeList({ label, items, tone }: { label: string; items: string[]; tone: string }) {
  if (items.length === 0) return null;
  return (
    <span className="ml-1">
      <span className="text-neutral-500">{label}: </span>
      <span className={tone}>{items.join("; ")}</span>
    </span>
  );
}

function BatchView({ batch }: { batch: PMScheduleBatch }) {
  const { dependencies } = batch;
  const hasLinks =
    dependencies.add.length + dependencies.update.length + dependencies.remove.length > 0;
  return (
    <div className="flex flex-col gap-1.5">
      {batch.project ? <Row label="Project">{batch.project}</Row> : null}
      {batch.targets.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="font-medium text-neutral-600">
            Rescheduled ({batch.targets.length}):
          </span>
          <MoveList moves={batch.targets} />
        </div>
      ) : null}
      {hasLinks ? (
        <div className="text-neutral-700">
          <span className="font-medium">Dependencies:</span>
          <ChangeList label="add" items={dependencies.add} tone="text-primary" />
          <ChangeList label="change" items={dependencies.update} tone="text-primary" />
          <ChangeList label="remove" items={dependencies.remove} tone="text-red-600" />
        </div>
      ) : null}
      <ScheduleEffect effect={batch} />
      {batch.baseline_save ? <Row label="Save baseline">{batch.baseline_save}</Row> : null}
      {batch.baseline_delete ? (
        <Row label="Delete baseline">
          <span className="text-red-600">{batch.baseline_delete}</span>
        </Row>
      ) : null}
    </div>
  );
}

function DiffRow({ row }: { row: PMIssueDiff }) {
  switch (row.field) {
    case "title":
    case "status":
    case "project":
    case "start_date":
    case "due_date":
      return (
        <>
          <span className="font-medium capitalize">{row.field.replace("_", " ")}:</span>{" "}
          <span className="text-neutral-500">{row.current ?? "—"}</span>
          <span className="px-1 text-neutral-400">→</span>
          <span className="text-primary">{row.proposed ?? "—"}</span>
        </>
      );
    case "disciplines":
      return (
        <>
          <span className="font-medium">Disciplines:</span>{" "}
          <span className="text-neutral-500">
            {row.current.length ? row.current.map(disciplineLabel).join(", ") : "—"}
          </span>
          <span className="px-1 text-neutral-400">→</span>
          <span className="text-primary">
            {row.proposed.length ? row.proposed.map(disciplineLabel).join(", ") : "—"}
          </span>
        </>
      );
    case "assignees":
      return (
        <>
          <span className="font-medium">Assignees:</span>{" "}
          <span className="text-neutral-500">
            {row.current.length ? row.current.map(userLabel).join(", ") : "—"}
          </span>
          <span className="px-1 text-neutral-400">→</span>
          <span className="text-primary">
            {row.proposed.length ? row.proposed.map(userLabel).join(", ") : "—"}
          </span>
        </>
      );
    case "linked_documents":
      return (
        <>
          <span className="font-medium">Linked documents:</span>
          {row.proposed.add.length > 0 ? (
            <span className="ml-1">
              <span className="text-neutral-500">add: </span>
              <span className="text-primary">{row.proposed.add.join(", ")}</span>
            </span>
          ) : null}
          {row.proposed.remove.length > 0 ? (
            <span className="ml-1">
              <span className="text-neutral-500">remove: </span>
              <span className="text-red-600">{row.proposed.remove.join(", ")}</span>
            </span>
          ) : null}
        </>
      );
    case "parent_issue":
      return (
        <>
          <span className="font-medium">Parent issue:</span>{" "}
          <span className="text-neutral-500">{parentLabel(row.current)}</span>
          <span className="px-1 text-neutral-400">→</span>
          <span className="text-primary">{parentLabel(row.proposed)}</span>
        </>
      );
    case "schedule_pinned":
      return (
        <>
          <span className="font-medium">Scheduling:</span>{" "}
          <span className="text-primary">
            {row.proposed ? "hand-scheduled (pinned)" : "follows its predecessors"}
          </span>
        </>
      );
    case "dependencies":
      return (
        <>
          <span className="font-medium">Depends on:</span>
          <ChangeList label="add" items={row.proposed.add} tone="text-primary" />
          <ChangeList label="remove" items={row.proposed.remove} tone="text-red-600" />
        </>
      );
    case "checklist":
      return (
        <>
          <span className="font-medium">Checklist:</span>
          <ChangeList label="add" items={row.proposed.add} tone="text-primary" />
          <ChangeList label="change" items={row.proposed.update} tone="text-primary" />
          <ChangeList label="remove" items={row.proposed.remove} tone="text-red-600" />
          {row.proposed.reorder.length > 0 ? (
            <ChangeList label="new order" items={row.proposed.reorder} tone="text-primary" />
          ) : null}
        </>
      );
  }
}

function AddCommentView({ payload }: { payload: AddPMCommentConfirmation }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="font-semibold text-neutral-800">{payload.summary}</div>
      <div className="whitespace-pre-wrap rounded-md border border-neutral-200 bg-white p-2 text-neutral-700">
        {payload.body}
      </div>
      {payload.mentions.length > 0 ? (
        <Row label="Mentions">{payload.mentions.map(userLabel).join(", ")}</Row>
      ) : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-1.5 text-neutral-700">
      <span className="font-medium text-neutral-600">{label}:</span>
      <span className="text-neutral-800">{children}</span>
    </div>
  );
}

function labelForStatus(status: string): string {
  return (
    {
      in_progress: "In progress",
      resolved: "Resolved",
      closed: "Closed",
    }[status] ?? status
  );
}
